<template>
  <v-dialog v-model="dialogVisible" max-width="700" persistent>
    <v-card class="mapping-card">
      <v-card-title class="primary white--text">
        <v-icon dark class="mr-2">settings_input_component</v-icon>
        Manage Sensors - {{ areaName }}
        <v-spacer />
        <v-btn icon dark @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-tabs v-model="tab" background-color="grey lighten-4" grow>
          <v-tab>
            <v-icon left>check_circle</v-icon>
            Assigned ({{ assignedSensors.length }})
          </v-tab>
          <v-tab>
            <v-icon left>add_circle</v-icon>
            Available ({{ unassignedSensors.length }})
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Assigned Sensors Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text v-if="assignedSensors.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey lighten-1">sensors_off</v-icon>
                <div class="text-body-1 grey--text mt-2">No sensors assigned to this area</div>
              </v-card-text>

              <v-list v-else dense class="pa-2">
                <v-list-item
                  v-for="sensor in assignedSensors"
                  :key="sensor.pi_id"
                  class="mb-2 elevation-1 rounded"
                >
                  <v-list-item-icon>
                    <v-icon :color="sensor.status === 'online' ? 'success' : 'error'">
                      {{ sensor.status === 'online' ? 'wifi' : 'wifi_off' }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>{{ sensor.client_name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ sensor.description || sensor.pi_id }}</v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      color="error"
                      :loading="removing === sensor.pi_id"
                      @click="removeSensor(sensor)"
                    >
                      <v-icon small>remove_circle</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-tab-item>

          <!-- Unassigned Sensors Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text v-if="loadingUnassigned" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-2">Loading available sensors...</div>
              </v-card-text>

              <v-card-text v-else-if="unassignedSensors.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey lighten-1">check_circle</v-icon>
                <div class="text-body-1 grey--text mt-2">All sensors are assigned to areas</div>
              </v-card-text>

              <v-list v-else dense class="pa-2">
                <v-list-item
                  v-for="sensor in unassignedSensors"
                  :key="sensor.pi_id"
                  class="mb-2 elevation-1 rounded"
                >
                  <v-list-item-icon>
                    <v-icon :color="sensor.status === 'online' ? 'success' : 'grey'">
                      {{ sensor.status === 'online' ? 'wifi' : 'wifi_off' }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>{{ sensor.client_name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ sensor.description || sensor.pi_id }}</v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      color="success"
                      :loading="adding === sensor.pi_id"
                      @click="addSensor(sensor)"
                    >
                      <v-icon small>add_circle</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn text color="primary" @click="loadUnassignedSensors">
          <v-icon left>refresh</v-icon>
          Refresh
        </v-btn>
        <v-spacer />
        <v-btn color="primary" @click="close">
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SensorMappingDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    areaId: {
      type: String,
      default: ''
    },
    areaName: {
      type: String,
      default: ''
    },
    sensors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      tab: 0,
      unassignedSensors: [],
      loadingUnassigned: false,
      adding: null,
      removing: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value)
          this.$emit('close')
      }
    },
    assignedSensors() {
      return [...this.sensors].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''))
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.tab = 0
        this.loadUnassignedSensors()
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async loadUnassignedSensors() {
      this.loadingUnassigned = true
      try {
        const response = await axios.get('/api/sensors/unassigned')
        this.unassignedSensors = response.data
      } catch (error) {
        console.error('Failed to load unassigned sensors:', error)
        this.unassignedSensors = []
      } finally {
        this.loadingUnassigned = false
      }
    },
    async addSensor(sensor) {
      this.adding = sensor.pi_id
      try {
        await axios.post(`/api/areas/${this.areaId}/sensors`, {pi_id: sensor.pi_id})
        this.unassignedSensors = this.unassignedSensors.filter(s => s.pi_id !== sensor.pi_id)
        this.$emit('updated')
      } catch (error) {
        console.error('Failed to add sensor:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to add sensor'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.adding = null
      }
    },
    async removeSensor(sensor) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      if (!window.confirm(`Remove "${sensor.client_name}" from this area?`))
        return

      this.removing = sensor.pi_id
      try {
        await axios.delete(`/api/areas/${this.areaId}/sensors/${sensor.pi_id}`)
        this.unassignedSensors.push(sensor)
        this.$emit('updated')
      } catch (error) {
        console.error('Failed to remove sensor:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to remove sensor'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.removing = null
      }
    }
  }
}
</script>

<style scoped>
.mapping-card {
  border-radius: 12px !important;
}
.v-list-item {
  background: white;
}
</style>
